import { Controller } from "@hotwired/stimulus";

// Reveal targets fade/translate into view as they cross 150px from the
// bottom of the viewport. Previously this ran on every scroll event and
// did a getBoundingClientRect() read followed by a classList.add() write
// per target in a single loop — every iteration after the first forced
// the browser to recompute layout (read after write). Lighthouse measured
// ~57ms of forced-reflow on the homepage from this controller.
//
// IntersectionObserver moves the geometry math off the main thread and
// fires once per crossing — no reflow, no scroll listener.
export default class extends Controller {
  static targets = ["reveal"];

  HOW_FAR_FROM_TOP_TO_REVEAL_IN_PIXELS = 150;

  connect() {
    this.prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (!this.prefersReducedMotion) {
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add("active");
              this.observer.unobserve(entry.target);
            }
          }
        },
        {
          // Fire as soon as any pixel of the target crosses 150px from the
          // bottom of the viewport — mirrors the old threshold.
          rootMargin: `0px 0px -${this.HOW_FAR_FROM_TOP_TO_REVEAL_IN_PIXELS}px 0px`,
        }
      );
    }

    this.revealTargets.forEach((element) => this.reveal(element));
  }

  // Catches targets that connect after this controller does. connect() used to read
  // revealTargets once and never again — if the parser hadn't reached them yet, nothing
  // got observed and the content sat at opacity:0 forever (.reveal is invisible until
  // .active lands).
  revealTargetConnected(element) {
    this.reveal(element);
  }

  // Called from connect()+revealTargetConnected(), usually runs twice
  // per element. observing + class adding are both noops on repeaat
  reveal(element) {
    if (this.prefersReducedMotion) {
      element.classList.add("active");
      return;
    }

    this.observer?.observe(element);
  }

  disconnect() {
    this.observer?.disconnect();
  }
}
